// Web-only Apple (Sign in with Apple JS, popup) and Google (Identity Services) helpers.
// Each provider is shown on web only when its client ID is configured at build time.
import { isNativePlatform } from './native';

export const APPLE_SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID;
export const GOOGLE_WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;

const APPLE_JS = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const GOOGLE_GSI = 'https://accounts.google.com/gsi/client';

export const isWebAppleEnabled = () => !isNativePlatform() && !!APPLE_SERVICE_ID;
export const isWebGoogleEnabled = () => !isNativePlatform() && !!GOOGLE_WEB_CLIENT_ID;

const _scripts = {};
export const loadScript = (src) => {
  if (!_scripts[src]) {
    _scripts[src] = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.onload = resolve;
      el.onerror = () => { delete _scripts[src]; reject(new Error('Could not load sign-in. Check your connection.')); };
      document.head.appendChild(el);
    });
  }
  return _scripts[src];
};

const _randomNonce = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/** Apple popup → credential in the same shape the native helper returns. */
export const getAppleWebCredential = async (serverNonce) => {
  await loadScript(APPLE_JS);
  const nonce = serverNonce || _randomNonce();
  window.AppleID.auth.init({
    clientId: APPLE_SERVICE_ID,
    scope: 'name email',
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    redirectURI: `${window.location.origin}/auth/apple/callback`,
    state: _randomNonce(),
    nonce,
    usePopup: true,
  });
  const result = await window.AppleID.auth.signIn();
  const idToken = result?.authorization?.id_token;
  if (!idToken) throw new Error('Apple sign-in returned no token.');
  const n = result?.user?.name || {};
  return {
    identityToken: idToken,
    nonce,
    email: result?.user?.email,
    name: [n.firstName, n.lastName].filter(Boolean).join(' ').trim() || undefined,
  };
};

/** Load GIS and render Google's own button; the callback receives { idToken }. */
export const renderGoogleWebButton = async (container, onCredential, text = 'continue_with') => {
  await loadScript(GOOGLE_GSI);
  window.google.accounts.id.initialize({
    client_id: GOOGLE_WEB_CLIENT_ID,
    callback: (resp) => resp?.credential && onCredential({ idToken: resp.credential }),
    ux_mode: 'popup',
  });
  window.google.accounts.id.renderButton(container, {
    theme: 'filled_black', shape: 'pill', size: 'large', text,
    width: Math.min(container.offsetWidth || 320, 400),
  });
};
